import { ArrowButton } from "@components/Buttons"
import { useIntl } from "@components/Intl"
import { Box } from "@components/Layout"
import { useNotificationsUpdate } from "@components/Notifications"
import { breakpoints } from "@config/theme"
import formatPrice from "@lib/formatPrice"
import { getGraphQLClient } from "@lib/graphql-client"
import { useAddCustomCombinationToCart } from "@lib/hooks/use-add-custom-combination-to-cart"
import { useCart } from "@lib/hooks/use-cart"
import { useRemoveCustomCombinationFromCart } from "@lib/hooks/use-remove-custom-combination-from-cart"
import { useQuery } from "@tanstack/react-query"
import { motion } from "framer-motion"
import { useRouter } from "next/router"
import React from "react"
import useMedia from "use-media"
import * as S from "./elements"
import { getCustomCombinationItems } from "./helpers/get-custom-combination-items"
import { getExistingCustomCombinationCartItemIds } from "./helpers/get-existing-custom-combination-cart-item-ids"

const { sdk } = getGraphQLClient()

const Navigation: React.FC = () => {
  const router = useRouter()
  const stoneId = router.query.stone as string
  const frameId = router.query.frame as string
  const baseId = router.query.base as string
  const style = router.query.style as string
  const substyle = router.query.substyle as string
  const type = router.query.type as string
  const customCombinationId = router.query.customCombinationId as string
  const { messages, locale } = useIntl()
  const { data: cart } = useCart()
  const {
    addCustomCombinationToCart,
    isLoading: addCustomCombinationToCartIsLoading,
  } = useAddCustomCombinationToCart()
  const {
    removeCustomCombinationFromCart,
    isLoading: removeCustomCombinationFromCartIsLoading,
  } = useRemoveCustomCombinationFromCart()

  const isSmall = useMedia(`screen and (max-width: ${breakpoints.medium})`)
  const { addNotification } = useNotificationsUpdate()
  const isEditing = customCombinationId !== undefined
  const ids: string[] = []
  if (stoneId) {
    ids.push(stoneId)
  }
  if (frameId) {
    ids.push(frameId)
  }
  if (baseId) {
    ids.push(baseId)
  }
  const { data, error, isFetching } = useQuery(["articles", ids], () =>
    sdk.ArticlesById({ ids })
  )

  const addToCart = async () => {
    if (!stoneId || !baseId || !style || !type) {
      return null
    }

    if (customCombinationId && cart.items) {
      const existingItemCartIds = getExistingCustomCombinationCartItemIds({
        items: cart.items,
        customCombinationId,
      })

      // If the cartitem already exists, remove it and add the new one
      // if it doesn't, no biggie, just add the new one without replacing the old one
      if (existingItemCartIds.length > 0) {
        await removeCustomCombinationFromCart(existingItemCartIds)
      }

      const components = getCustomCombinationItems({
        stoneId,
        frameId,
        baseId,
        type: type === "earring" ? "earring" : "ring",
      })
      await addCustomCombinationToCart(components)
    } else {
      // If the cartitem doesnt exist yet, add all components to cart
      const components = getCustomCombinationItems({
        stoneId,
        frameId,
        baseId,
        type: type === "earring" ? "earring" : "ring",
      })

      await addCustomCombinationToCart(components)

      addNotification({
        type: "success",
        text: `${messages.notifications.addedToCart}`,
      })
    }
    router.push(locale === "en" ? "/en/shopping-bag" : "/nl/winkelmand")
  }

  let sum = 0
  const selectedBase =
    data && data.choicesArticlesById
      ? data.choicesArticlesById.filter((a) => a.component === "base")
      : null
  const selectedStone =
    data && data.choicesArticlesById
      ? data.choicesArticlesById.filter((a) => a.component === "stone")
      : null
  const selectedFrame =
    data && data.choicesArticlesById
      ? data.choicesArticlesById.filter((a) => a.component === "frame")
      : null

  if (type === "earring") {
    if (selectedBase && selectedBase[0] && selectedBase[0].price) {
      sum += selectedBase[0].price
    }
    if (
      style !== "hippiechic" &&
      selectedFrame &&
      selectedFrame[0] &&
      selectedFrame[0].price
    ) {
      sum += selectedFrame[0].price * 2
    }
    if (selectedStone && selectedStone[0] && selectedStone[0].price) {
      sum += selectedStone[0].price * 2
    }
  } else if (type === "ring") {
    if (selectedBase && selectedBase[0] && selectedBase[0].price) {
      sum += selectedBase[0].price
    }
    if (selectedFrame && selectedFrame[0] && selectedFrame[0].price) {
      sum +=
        substyle === "ring_base_twist_0808"
          ? selectedFrame[0].price * 2
          : selectedFrame[0].price
    }
    if (selectedStone && selectedStone[0] && selectedStone[0].price) {
      sum +=
        substyle === "ring_base_twist_0808"
          ? selectedStone[0].price * 2
          : selectedStone[0].price
    }
  }

  return (
    <>
      {error && null}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Box width="100%" display="flex" alignItems="center">
          <Box display={["none", "block"]}>
            <ArrowButton
              onClick={() => router.back()}
              variant="arrow-left"
              styling="purple-caps"
            >
              back
            </ArrowButton>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            width="100%"
            justifyContent="flex-end"
          >
            <Box mr="4" display={["none", "flex"]}>
              {sum && sum > 0 && !isSmall ? (
                <Box display="flex" alignItems="baseline">
                  <S.Total>{messages.cart.total}</S.Total>
                  <S.Price as="h4" width="8rem">
                    {isFetching ? "..." : formatPrice(sum)}
                  </S.Price>
                </Box>
              ) : null}
            </Box>
            <S.AddToBag
              variant="purple"
              icon="add-to-bag"
              maxWidth={["1000px", "16rem"]}
              state={
                addCustomCombinationToCartIsLoading ||
                removeCustomCombinationFromCartIsLoading
                  ? "loading"
                  : ""
              }
              onClick={() => {
                if (sum && sum > 0) {
                  addToCart()
                }
              }}
            >
              {isSmall ? (
                <>
                  {isEditing
                    ? `${sum ? formatPrice(sum) : ""} - ${
                        messages.global.update
                      }`
                    : `${sum ? formatPrice(sum) : ""} - ${
                        messages.global.addToBag
                      }`}
                </>
              ) : (
                <>
                  {isEditing
                    ? messages.global.update
                    : messages.global.addToBag}
                </>
              )}
            </S.AddToBag>
          </Box>
        </Box>
      </motion.div>
    </>
  )
}

export default React.memo(Navigation)
